import { useContextState } from "../../../state/use-context-state";
import type { MiningMode, MiningPowerMode } from "../types";

interface MiningPresentationInput {
  contextKey: string;
  actions: {
    setMiningMode(mode: MiningMode): void | Promise<void>;
    setMiningPowerMode(mode: MiningPowerMode): void | Promise<void>;
    startMining(): void | Promise<void>;
    pauseMining(): void | Promise<void>;
    resumeMining(): void | Promise<void>;
    retryMiningSubmission(): void | Promise<void>;
    checkMiningStatus?(): void | Promise<void>;
    startNewMiningSession?(): void | Promise<void>;
    checkSavedMiningProof?(): void | Promise<void>;
  };
}

interface MiningPresentationFeedback {
  invoke(action: () => void | Promise<void>, fallback: string): void;
  clearNotice(): void;
}

export function useMiningPresentation({ contextKey, actions }: MiningPresentationInput,
  { invoke, clearNotice }: MiningPresentationFeedback) {
  const [ui, setUi] = useContextState(contextKey, createUiState);
  const { settingsBusy, settingsPending } = ui;

  function changeMiningSettings(action: () => void | Promise<void>, fallback: string): void {
    // Block repeated clicks immediately, before React renders the busy state.
    if (settingsPending.current) return;
    settingsPending.current = true;
    setUi((current) => ({ ...current, settingsBusy: true }));
    clearNotice();
    invoke(async () => {
      try { await action(); }
      finally {
        settingsPending.current = false;
        setUi((current) => ({ ...current, settingsBusy: false }));
      }
    }, fallback);
  }

  return {
    settingsBusy,
    onModeChange: (mode: MiningMode) => changeMiningSettings(
      () => actions.setMiningMode(mode), "Mining compute mode could not be saved. Please try again."),
    onPowerChange: (mode: MiningPowerMode) => changeMiningSettings(
      () => actions.setMiningPowerMode(mode), "Mining power setting could not be saved. Please try again."),
    onStart: () => { if (!settingsPending.current) invoke(actions.startMining, "Mining could not start."); },
    onPause: () => invoke(actions.pauseMining, "Mining could not pause."),
    onResume: () => { if (!settingsPending.current) invoke(actions.resumeMining, "Mining could not resume."); },
    onRetrySubmission: () => invoke(actions.retryMiningSubmission, "Proof submission could not be retried."),
    onCheckStatus: () => { if (actions.checkMiningStatus) invoke(actions.checkMiningStatus, "Mining status could not be checked."); },
    onNewSession: () => { if (actions.startNewMiningSession) invoke(actions.startNewMiningSession, "A new mining session could not be created."); },
    onSavedProof: () => { if (actions.checkSavedMiningProof) invoke(actions.checkSavedMiningProof, "Saved proof could not be checked."); },
  };
}

function createUiState() {
  return {
    settingsBusy: false,
    // Immediate click guards belong to this UI generation, just like its busy state.
    settingsPending: { current: false },
  };
}
